import { App } from '@capacitor/app';
import { Badge } from '@capawesome/capacitor-badge';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';

const TOKEN_STORAGE_KEY = 'ecochil.fcm-token';
const ALLOWED_HOST = 'club.ecochil.net';

function tenantCode(): string | null {
  const segment = window.location.pathname.split('/').filter(Boolean)[0];
  return segment && /^[a-z0-9-]+$/.test(segment) ? segment : null;
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

function isAuthenticated(): boolean {
  return document.querySelector<HTMLMetaElement>('meta[name="app-authenticated"]')?.content === 'true';
}

function safeInAppTarget(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;

  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== 'https:' || url.host !== ALLOWED_HOST) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

async function registerToken(token: string): Promise<void> {
  const tenant = tenantCode();
  if (!tenant || !isAuthenticated()) return;

  const appInfo = await App.getInfo().catch(() => null);
  const response = await fetch(`/${tenant}/api/devices`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken(),
    },
    body: JSON.stringify({
      token,
      platform: Capacitor.getPlatform(),
      app_version: appInfo?.version ?? null,
    }),
  });

  if (response.ok) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }
}

/**
 * 現在ページが「push許可を求めるべき文脈」か判定。
 * ・投稿完了、お気に入り追加後、お知らせページなど「通知に価値がある状況」でのみ促す。
 * ・起動即のダイアログは拒否率が高いため避け、ユーザーが動機を感じた瞬間に出す。
 * サーバー側が session('ga_event') を <meta name="push-trigger"> として出力し、
 * bridge.ts はその meta を読んで判定する。
 */
function shouldPromptPushNow(): boolean {
  const trigger = document.querySelector<HTMLMetaElement>('meta[name="push-trigger"]')?.content ?? '';
  const path = window.location.pathname;
  // 明示的トリガ、または通知に関わる画面
  return (
    trigger === '1' ||
    /\/notifications(\/|$)/.test(path) ||
    /\/favorites(\/|$)/.test(path) ||
    /\/news(\/|$)/.test(path)
  );
}

async function setupPush(): Promise<void> {
  // Android通知チャネル：端末の「アプリ設定→通知」画面で日本語表示にする。
  // iOSはチャネルという概念がなく、appName（エコチルクラブ）がそのまま表示される。
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({
      id: 'ecochil_default',
      name: 'エコチルクラブからのお知らせ',
      description: '応援・お知らせ・イベントの通知',
      importance: 4,
      visibility: 1,
      lights: true,
      vibration: true,
    }).catch(() => undefined);
  }
  await PushNotifications.addListener('registration', () => {
    // iOSの公式Pushプラグインが返すのはAPNsトークンのため、FCMトークンへ変換して統一する。
    void FCM.getToken()
      .then(({ token }) => registerToken(token))
      .catch(() => undefined);
  });
  await PushNotifications.addListener('registrationError', () => {
    // 次回の画面表示時に再試行する。トークンやFirebase詳細はブラウザログに出さない。
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    const target = safeInAppTarget(event.notification.data?.url);
    if (target) window.location.assign(target);
  });

  const current = await PushNotifications.checkPermissions();

  // 既に許可済み → そのまま register
  if (current.receive === 'granted') {
    await PushNotifications.register();
    return;
  }

  // 未応答（prompt）状態でも、文脈のない画面ではダイアログを出さない。
  // 起動即ダイアログ拒否 → 一生 push OFF になるのを防ぐ。
  if (current.receive === 'prompt' && shouldPromptPushNow()) {
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive === 'granted') {
      await PushNotifications.register();
    }
  }
}

function setupExternalLinks(): void {
  document.addEventListener('click', (event) => {
    const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (!anchor) return;

    const url = new URL(anchor.href, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) return;
    if (url.origin === window.location.origin && anchor.target !== '_blank') return;

    event.preventDefault();
    void Browser.open({ url: url.href });
  }, true);
}

function setupLogoutCleanup(): void {
  document.querySelectorAll<HTMLFormElement>('form[action$="/logout"]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      const tenant = tenantCode();
      if (!token || !tenant || form.dataset.deviceCleanup === 'done') return;

      event.preventDefault();
      form.dataset.deviceCleanup = 'done';
      let submitted = false;
      const submit = () => {
        if (submitted) return;
        submitted = true;
        form.submit();
      };
      const timeout = window.setTimeout(submit, 800);

      void fetch(`/${tenant}/api/devices/${encodeURIComponent(token)}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
        },
      }).finally(() => {
        void FCM.deleteInstance().catch(() => undefined);
        window.clearTimeout(timeout);
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        submit();
      });
    });
  });
}

async function setupNativeNavigation(): Promise<void> {
  await App.addListener('appUrlOpen', (event) => {
    const target = safeInAppTarget(event.url);
    if (target) window.location.assign(target);
  });

  if (Capacitor.getPlatform() === 'android') {
    await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else if (window.confirm('アプリを終了しますか？')) {
        void App.exitApp();
      }
    });
  }
}

async function updateAppBadge(): Promise<void> {
  // <meta name="unread-notif-count" content="N"> をヘッダーレイアウトから出力しておく前提。
  // ページ遷移毎に読んで最新値を反映する（ポーリング不要）。
  const meta = document.querySelector<HTMLMetaElement>('meta[name="unread-notif-count"]');
  const raw = meta?.content ?? '0';
  const count = Math.max(0, parseInt(raw, 10) || 0);
  try {
    const permitted = await Badge.isSupported();
    if (!permitted?.isSupported) return;

    // 初回のみパーミッション確認（拒否/未応答なら黙って諦める）
    const permStatus = await Badge.checkPermissions().catch(() => null);
    if (permStatus?.display === 'prompt') {
      await Badge.requestPermissions().catch(() => undefined);
    }

    if (count > 0) {
      await Badge.set({ count });
    } else {
      await Badge.clear();
    }
  } catch {
    // バッジは補助機能なので失敗しても本体機能は継続
  }
}

/**
 * 引っぱって更新（プルトゥリフレッシュ）。
 * ページ最上部でさらに下へ引くとインジケータが出て、しきい値を超えて離すとリロードする。
 * 横スワイプやページ途中でのスクロールでは反応しない。
 */
function setupPullToRefresh(): void {
  const THRESHOLD = 70;   // これ以上引いたらリロード
  const MAX_PULL = 110;   // インジケータの移動上限

  const indicator = document.createElement('div');
  indicator.setAttribute('aria-hidden', 'true');
  indicator.style.cssText = [
    'position:fixed', 'top:0', 'left:50%', 'z-index:9999',
    'width:36px', 'height:36px', 'margin-left:-18px', 'border-radius:9999px',
    'background:#fff', 'box-shadow:0 2px 8px rgba(0,0,0,.18)',
    'display:flex', 'align-items:center', 'justify-content:center',
    'pointer-events:none', 'opacity:0',
    'transform:translateY(-48px)',
  ].join(';');
  indicator.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2f8f6b" stroke-width="2.5" stroke-linecap="round">' +
    '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>';
  document.body.appendChild(indicator);

  let startY = 0;
  let startX = 0;
  let pulled = 0;
  let pulling = false;
  let refreshing = false;

  const reset = (): void => {
    indicator.style.transition = 'transform .2s ease, opacity .2s ease';
    indicator.style.transform = 'translateY(-48px)';
    indicator.style.opacity = '0';
    pulling = false;
    pulled = 0;
  };

  document.addEventListener('touchstart', (event) => {
    if (refreshing || event.touches.length !== 1) return;
    // 画像ビューアなどのモーダル内では反応しない
    const target = event.target as Element | null;
    if (target?.closest?.('[data-no-pull-refresh]')) return;
    // ページ最上部のときだけ受け付ける
    if (window.scrollY > 0) return;
    startY = event.touches[0].clientY;
    startX = event.touches[0].clientX;
    pulling = true;
    indicator.style.transition = 'none';
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    if (!pulling || refreshing) return;
    const dy = event.touches[0].clientY - startY;
    const dx = Math.abs(event.touches[0].clientX - startX);
    // 上方向・横スワイプ・スクロールが進んだ場合は中断（横スクロールのカード列を邪魔しない）
    if (dy <= 0 || dx > Math.abs(dy) || window.scrollY > 0) {
      reset();
      return;
    }
    pulled = Math.min(dy * 0.5, MAX_PULL);
    indicator.style.opacity = String(Math.min(pulled / THRESHOLD, 1));
    indicator.style.transform = `translateY(${pulled - 40}px) rotate(${pulled * 3}deg)`;
  }, { passive: true });

  const finish = (): void => {
    if (!pulling || refreshing) { reset(); return; }
    if (pulled >= THRESHOLD) {
      refreshing = true;
      indicator.style.transition = 'transform .2s ease';
      indicator.style.transform = 'translateY(16px)';
      indicator.style.opacity = '1';
      window.location.reload();
      return;
    }
    reset();
  };

  document.addEventListener('touchend', finish, { passive: true });
  document.addEventListener('touchcancel', reset, { passive: true });
}

async function boot(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  document.body.classList.add('in-app');
  setupExternalLinks();
  setupLogoutCleanup();
  setupPullToRefresh();
  await setupNativeNavigation();
  if (isAuthenticated()) {
    await setupPush();
    void updateAppBadge();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot(), { once: true });
} else {
  void boot();
}
